import { Box, Stack, SxProps, Theme, Typography } from "@mui/material";
import { Moment } from "moment";
import { useMemo } from "react";

import Datetime from "@/common/components/Datetime/Datetime";
import { DateRangeDurationHumanizeOptions, DatetimeHelper } from "@/common/helpers/datetime";
import { renderIf } from "@/common/helpers/render/renderIf";

import AppTooltip from "../Tooltip/AppTooltip";

export type IDatetimeRange = {
  startsAt: string | Moment | null | undefined;
  endsAt: string | Moment | null | undefined;
};

interface Props {
  range: IDatetimeRange;
  durationHumanizeOptions?: DateRangeDurationHumanizeOptions;
  variant?: "normal" | "compact";
  direction?: "row" | "column";
  withDuration?: boolean;
  withTooltip?: boolean;
  sx?: SxProps<Theme>;
}

export default function DatetimeRange({
  range,
  durationHumanizeOptions = { isSuffix: false },
  variant = "normal",
  direction = "row",
  withDuration = false,
  withTooltip = true,
  sx,
}: Props) {
  const durationHumanized = useMemo(
    () =>
      DatetimeHelper.humanizeDateRangeDuration(
        range.startsAt,
        range.endsAt,
        durationHumanizeOptions,
      ),
    [range.startsAt, range.endsAt],
  );

  if (!range.startsAt && !range.endsAt) {
    return <Box component='span'>-</Box>;
  }

  return (
    <AppTooltip
      sx={{ display: "inline" }}
      isEnabled={withTooltip}
      variant='helpText'
      title={
        <Typography component='div' variant='body2' sx={{ whiteSpace: "nowrap" }}>
          <Stack>
            <Box>
              Start: <Datetime datetime={range.startsAt} />
            </Box>
            <Box>
              End: <Datetime datetime={range.endsAt} />
            </Box>
            <Box>
              Duration: {durationHumanized || "-"}
              {/* {DatetimeHelper.humanizeDateRangeDuration(range.startsAt, range.endsAt, {
                isSuffix: false,
              })} */}
            </Box>
          </Stack>
        </Typography>
      }
    >
      <Stack
        component='span'
        direction={direction}
        spacing={direction === "row" ? 0.5 : 0}
        sx={{
          display: "inline-flex",
          ...((direction === "row" && { alignItems: "center", justifyContent: "start" }) || {}),
          ...((direction === "column" && { alignItems: "start", justifyContent: "start" }) || {}),
          ...sx,
        }}
      >
        {variant === "normal" && (
          <>
            <Box component='span'>
              <Datetime datetime={range.startsAt} />
            </Box>
            {range.endsAt && direction === "row" && <Box component='span'>{` - `}</Box>}
            {range.endsAt && (
              <Box component='span'>
                <Datetime datetime={range.endsAt} />
              </Box>
            )}
            {withDuration && durationHumanized && (
              <Typography component='span' color='text.secondary' fontSize='smaller'>
                {" "}
                ({durationHumanized})
              </Typography>
            )}
          </>
        )}

        {variant === "compact" &&
          renderIf()
            .if(!!durationHumanized)
            .then(<Box component='span'>{durationHumanized}</Box>)
            .else(
              <>
                <Box component='span'>
                  {range.startsAt ? <Datetime datetime={range.startsAt} /> : "-"}
                </Box>
                {range.endsAt && direction === "row" && <Box component='span'>{` - `}</Box>}
                <Box component='span'>
                  {range.endsAt ? <Datetime datetime={range.endsAt} /> : "-"}
                </Box>
              </>,
            )
            .render()}
      </Stack>
    </AppTooltip>
  );
}
